import { useCurrentPath } from "@/hooks";
import { useDataStore, useFilterStore, useLocaleStore } from "@/stores";
import { useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import ContentEditable from "react-contenteditable";
import { generatePath, Link, useParams } from "react-router-dom";
import Settings from "./Settings";

interface ButtonProps {
    text: string;
    href?: string;
    internalPageId?: string;
    internalElementSlug?: string;
    target?: "_blank" | "_self" | "_parent" | "_top";
}

const SpotlightButton = ({ text, href, internalPageId, internalElementSlug, target }: Partial<ButtonProps>) => {
    const userPages = useDataStore((state) => state.pages);
    const userElements = useDataStore((state) => state.elements);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);
    const activeVersion = useFilterStore((state) => state.activeVersion);
    const [link, setLink] = useState<{ link: string; target: string }>({ link: "#", target: "_self" });

    const editableRef = useRef<HTMLDivElement | null>(null);
    const {
        connectors: { connect },
        actions: { setProp },
    } = useNode((node) => ({
        parentNodeId: node.data.parent,
    }));
    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled,
    }));
    const params = useParams();
    const currentPath = useCurrentPath();
    useEffect(() => {
        // Fix for nested divs being editable when they shouldn't exist due to a previous bug with pasting content.
        if (!editableRef.current) return;

        const editableDivs = editableRef.current.querySelectorAll("div[contenteditable='true']");

        for (let i = 0; i < editableDivs.length; i++) {
            const editableDiv = editableDivs[i] as HTMLDivElement;
            editableDiv.contentEditable = enabled.toString();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableRef.current, enabled]);

    useEffect(() => {
        // Fix for pasted content sometimes having font-size attribute.
        if (!editableRef.current) return;

        const elementsWithFontSize = editableRef.current.querySelectorAll("[style*='font-size']");

        for (let i = 0; i < elementsWithFontSize.length; i++) {
            const element = elementsWithFontSize[i] as HTMLElement;
            element.style.fontSize = "";
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableRef.current, enabled]);

    useEffect(() => {
        if (enabled || !activeLanguage) return;

        fixInternalLink();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, enabled]);

    const fixInternalLink = () => {
        const pageId = internalPageId;
        const elementSlug = internalElementSlug;
        const link = { link: "#", target: target || "_self" };

        let path = currentPath;
        if (path === "/:locale") path = "/:locale/*";

        if (pageId) {
            const page = userPages.find((page) => page.id === pageId);
            if (page) {
                link.link = generatePath(path, { ...params, "*": page.url }).replace(":/", "");
            }
        } else if (elementSlug) {
            const elementPage = userElements[activeVersion!]?.find((page) => page.slug === elementSlug);
            if (elementPage) {
                link.link = generatePath(path, { ...params, "*": `elements/${elementSlug}` }).replace(":/", "");
            }
        } else if (href) {
            link.link = href;
        }

        setLink(link);
    };

    return (
        <Link
            to={link.link}
            target={link.target}
            ref={(ref) => connect(ref!)}
            className={clsx([
                "flex text-white font-medium text-base/normal px-5 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg mx-auto gap-3",
                enabled ? "cursor-auto" : "cursor-pointer",
                "no-underline",
            ])}
        >
            <ContentEditable
                html={text || ""}
                innerRef={editableRef}
                disabled={!enabled}
                onChange={(e) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setProp((props: any) => (props.text = e.target.value), 500);
                }}
                tagName="span"
                is={"span"}
                className="font-medium text-base/normal"
            />
        </Link>
    );
};

export default SpotlightButton;

SpotlightButton.craft = {
    displayName: "Action",
    props: {
        text: "Text",
    },
    related: {
        settings: Settings,
    },
    rules: {
        canDrag: () => false,
        canDrop: () => false,
    },
};
